import { useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { supabase } from "../../supabase";
import "./MyPage.css";
import { DESIGN_SAVE_LIMIT, FILE_SAVE_LIMIT, PRICING_OPEN } from "../../config/features";
import { countSaved } from "../../utils/storageQuota";
import { useHistory } from "react-router-dom";
import { validateNickname } from "../../utils/validateNickname";
import { useTranslation } from "react-i18next";
import { useModal } from "../../context/ModalContext";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

export default function MyPageAccount() {
  const { user, refreshProfile } = useContext(AuthContext);
  const history = useHistory();
  const { t } = useTranslation();
  const { showAlert } = useModal();

  const joinDate = (() => {
    const d = user?.created_at ? new Date(user.created_at) : null;
    if (!d || Number.isNaN(d.getTime())) return "-";
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
  })();

  const provider = user?.app_metadata?.provider || user?.provider || "email";
  const isSocialLogin = provider !== "email";

  const [isAdmin, setIsAdmin] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [apiPlan, setApiPlan] = useState(null);

  const plan = apiPlan ?? user?.plan ?? "FREE";

  const fetchSubscription = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${API_BASE}/subscription/status`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      setSubscription(data.subscription);
      if (data.plan) setApiPlan(data.plan);
    } catch (e) {
      console.error("subscription fetch error", e);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // ✅ 탈퇴 모달용 state
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawPw, setWithdrawPw] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);

  // 🔥 비밀번호 보기 토글
  const [showWithdrawPw, setShowWithdrawPw] = useState(false);

  const [savedCount, setSavedCount] = useState({ designs: 0, files: 0 });

  const [aiUsage, setAiUsage] = useState(null);

  const [currentUsername, setCurrentUsername] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [usernameLoading, setUsernameLoading] = useState(true);
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [usernameError, setUsernameError] = useState("");
  const [usernameSuccess, setUsernameSuccess] = useState("");

  useEffect(() => {
    async function checkAdmin() {
      if (!user?.id) {
        setIsAdmin(false);
        return;
      }

      const { data } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", user.id)
        .single();

      setIsAdmin(data?.role === "admin");
    }

    checkAdmin();
  }, [user?.id]);

  // 🔽 여기부터 추가
  useEffect(() => {
    if (!user?.id) return;

    async function fetchUsage() {
      setSavedCount(await countSaved(user.id));

      // AI 디자인 사용량
      try {
        const aiRes = await fetch(`/ai/usage?user_id=${user.id}`);
        if (aiRes.ok) setAiUsage(await aiRes.json());
      } catch {}
    }

    fetchUsage();
  }, [user?.id]);

  useEffect(() => {
    async function fetchUsername() {
      setUsernameLoading(true);

      const {
        data: { user: authUser },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !authUser?.id) {
        setUsernameError(t("mypage.account.nicknameFetchError"));
        setUsernameLoading(false);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("username")
        .eq("id", authUser.id)
        .single();

      if (profileError) {
        setUsernameError(t("mypage.account.nicknameFetchError"));
        setUsernameLoading(false);
        return;
      }

      const loadedUsername = profile?.username || "";
      setCurrentUsername(loadedUsername);
      setNewUsername(loadedUsername);
      setUsernameError("");
      setUsernameLoading(false);
    }

    fetchUsername();
  }, []);

  async function saveUsername() {
    if (usernameSaving) return;

    setUsernameError("");
    setUsernameSuccess("");

    const trimmed = newUsername.trim();
    const nicknameValidationError = validateNickname(trimmed);

    if (nicknameValidationError) {
      setUsernameError(nicknameValidationError);
      return;
    }

    setUsernameSaving(true);

    const { data: duplicated, error: duplicateError } = await supabase
      .from("profiles")
      .select("id")
      .eq("username", trimmed)
      .neq("id", user.id)
      .maybeSingle();

    if (duplicateError) {
      setUsernameError(t("mypage.account.nicknameDuplicateError"));
      setUsernameSaving(false);
      return;
    }

    if (duplicated) {
      setUsernameError(t("mypage.account.nicknameAlreadyUsed"));
      setUsernameSaving(false);
      return;
    }

    const { data: currentProfile, error: profileCheckError } = await supabase
      .from("profiles")
      .select("username_changed_at")
      .eq("id", user.id)
      .single();

    if (profileCheckError) {
      setUsernameError(t("mypage.account.nicknameCheckError"));
      setUsernameSaving(false);
      return;
    }

    const lastChangedAt = currentProfile?.username_changed_at
      ? new Date(currentProfile.username_changed_at)
      : null;

    if (lastChangedAt) {
      const now = new Date();
      const diffMs = now.getTime() - lastChangedAt.getTime();
      const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;

      if (diffMs < thirtyDaysMs) {
        setUsernameError(t("mypage.account.nickname30DayLimit"));
        setUsernameSaving(false);
        return;
      }
    }

    const { error: updateError } = await supabase
      .from("profiles")
      .update({
        username: trimmed,
        username_changed_at: new Date().toISOString(),
      })
      .eq("id", user.id);

    if (updateError) {
      setUsernameError(t("mypage.account.nicknameUpdateFailed"));
      setUsernameSaving(false);
      return;
    }

    await refreshProfile();

    setCurrentUsername(trimmed);
    setNewUsername(trimmed);
    setUsernameSuccess(t("mypage.account.nicknameUpdated"));
    setUsernameSaving(false);
  }
  // 🔼 여기까지

  const storageRatio = Math.min(1, Math.max(
    savedCount.designs / DESIGN_SAVE_LIMIT,
    savedCount.files / FILE_SAVE_LIMIT,
  ));
  const avatarChar = (currentUsername || user?.username || user?.email || "?").charAt(0).toUpperCase();

  return (
    <div className="mp-account">
      <div className="mp-page-head">
        <div>
          <h1 className="mp-page-title">{t("mypage.account.title")}</h1>
          <p className="mp-page-sub">{t("mypage.account.desc")}</p>
        </div>
      </div>

      {/* ===== 프로필 ===== */}
      <section className="mp-box">
        <h3>{t("mypage.account.profileTitle")}</h3>
        <div className="mp-profile">
          <span className="mp-avatar big">{avatarChar}</span>
          <div className="mp-profile-text">
            <b>
              {user?.email ?? "-"}
              {isSocialLogin && (
                <span className="provider-label">
                  {provider === "google" ? "Google" : provider}
                </span>
              )}
              {isAdmin ? (
                <span className="plan-badge admin">ADMIN</span>
              ) : PRICING_OPEN ? (
                <span className="plan-badge">{plan}</span>
              ) : null}
            </b>
            <small>{t("mypage.account.joinDate")} {joinDate}</small>

            {PRICING_OPEN && subscription?.status === "active" && (
              <div className="subscription-info">
                {t("mypage.account.subscriptionActive", {
                  plan: plan,
                  cycle: subscription.billing_cycle === "yearly"
                    ? t("mypage.account.cycleYearly")
                    : t("mypage.account.cycleMonthly"),
                  date: subscription.current_period_end
                    ? new Date(subscription.current_period_end).toLocaleDateString()
                    : "-",
                })}
              </div>
            )}

            {PRICING_OPEN && subscription?.status === "cancelled" && (
              <div className="subscription-info cancelled">
                {t("mypage.account.subscriptionCancelled", {
                  plan: plan,
                  date: subscription.current_period_end
                    ? new Date(subscription.current_period_end).toLocaleDateString()
                    : "-",
                })}
              </div>
            )}
          </div>
        </div>

        <label className="mp-label" htmlFor="mp-nickname">{t("mypage.account.nickname")}</label>
        <div className="mp-input-row">
          <input
            id="mp-nickname"
            type="text"
            value={newUsername}
            minLength={2}
            maxLength={20}
            disabled={usernameLoading || usernameSaving}
            onChange={(e) => {
              setNewUsername(e.target.value);
              if (usernameError) setUsernameError("");
              if (usernameSuccess) setUsernameSuccess("");
            }}
            placeholder={usernameLoading ? t("mypage.account.loading") : t("mypage.account.newNicknamePlaceholder")}
          />
          <button
            type="button"
            className="mp-btn dark"
            onClick={saveUsername}
            disabled={usernameLoading || usernameSaving}
          >
            {usernameSaving ? t("mypage.account.saving") : t("mypage.home.save")}
          </button>
        </div>

        {usernameError && <div className="mp-msg error">{usernameError}</div>}
        {usernameSuccess && <div className="mp-msg ok">{usernameSuccess}</div>}
      </section>

      {/* ===== 사용 현황 ===== */}
      <section className="mp-box">
        <h3>{t("mypage.account.usageTitle")}</h3>
        <div className="mp-usage">
          <div>
            <small>{t("mypage.account.storageLabel")}</small>
            <b>
              {t("mypage.account.storageDesigns")} {savedCount.designs}/{DESIGN_SAVE_LIMIT} · STL {savedCount.files}/{FILE_SAVE_LIMIT}
            </b>
            <div className="mp-bar"><span style={{ width: `${Math.round(storageRatio * 100)}%` }} /></div>
          </div>

          <div>
            <small>{t("mypage.account.todayAiDesign")}</small>
            <b>
              {aiUsage
                ? aiUsage.limit === -1
                  ? t("mypage.storage.unlimited")
                  : `${aiUsage.used} / ${aiUsage.limit}${t("mypage.storage.uses")}`
                : "-"}
            </b>
            {aiUsage && aiUsage.limit > 0 && (
              <div className="mp-bar">
                <span style={{ width: `${Math.min((aiUsage.used / aiUsage.limit) * 100, 100)}%` }} />
              </div>
            )}
          </div>

          <div>
            <small>{t("mypage.storage.stlDownload")}</small>
            <b>{t("mypage.storage.unlimited")}</b>
          </div>
        </div>
      </section>

      {/* ===== 계정 설정 ===== */}
      <section className="mp-box">
        <h3>{t("mypage.account.settingsTitle")}</h3>

        {isSocialLogin && (
          <div className="mp-social-note">
            {t("mypage.account.socialLoginNotice", { provider: provider === "google" ? "Google" : provider })}
          </div>
        )}

        <div className="mp-settings-row">
          {!isSocialLogin ? (
            <button
              type="button"
              className="mp-btn outline"
              onClick={() => {
                history.push("/update-password");
              }}
            >
              {t("mypage.account.changePassword")}
            </button>
          ) : <span />}

          <button
            type="button"
            className="mp-text-link"
            onClick={() => setWithdrawOpen(true)}
          >
            {t("mypage.account.withdraw")}
          </button>
        </div>
      </section>

      {/* ===== 탈퇴 확인 모달 ===== */}
      {withdrawOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{t("mypage.account.withdrawModalTitle")}</h3>
            <p>{isSocialLogin
              ? t("mypage.account.withdrawModalDescSocial")
              : t("mypage.account.withdrawModalDesc")
            }</p>

            {!isSocialLogin && (
              /* 비밀번호 + 👁 */
              <div className="password-wrapper">
                <input
                  type={showWithdrawPw ? "text" : "password"}
                  placeholder={t("mypage.account.passwordPlaceholder")}
                  value={withdrawPw}
                  onChange={(e) => setWithdrawPw(e.target.value)}
                />

                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowWithdrawPw((p) => !p)}
                  aria-label={t("mypage.account.passwordToggle")}
                >
                  {showWithdrawPw ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 3l18 18" />
                      <path d="M10.58 10.58A3 3 0 0 0 12 15a3 3 0 0 0 1.42-.42" />
                      <path d="M9.88 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a18.6 18.6 0 0 1-3.17 4.57" />
                      <path d="M6.61 6.61A18.6 18.6 0 0 0 1 12s4 8 11 8a10.94 10.94 0 0 0 4.76-1.06" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            )}

            <div className="modal-actions">
              <button
                onClick={() => {
                  setWithdrawOpen(false);
                  setWithdrawPw("");
                  setShowWithdrawPw(false);
                }}
              >
                {t("mypage.account.cancel")}
              </button>

              <button
                className="danger"
                disabled={withdrawing}
                onClick={async () => {
                  console.log("탈퇴 클릭", user, withdrawPw);

                  if (!isSocialLogin) {
                    if (!withdrawPw) {
                      showAlert(t("mypage.account.enterPassword"));
                      return;
                    }

                    // 1️⃣ 재인증
                    const { error } = await supabase.auth.signInWithPassword({
                      email: user.email,
                      password: withdrawPw,
                    });

                    if (error) {
                      showAlert(t("mypage.account.wrongPassword"));
                      return;
                    }
                  }

                  // 2️⃣ 토큰 재확보
                  const {
                    data: { session },
                  } = await supabase.auth.getSession();

                  setWithdrawing(true);
                  try {
                    const res = await fetch(`${API_BASE}/delete-account`, {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${session.access_token}`,
                      },
                    });

                    if (!res.ok) {
                      setWithdrawing(false);
                      showAlert(t("mypage.account.withdrawError"));
                      return;
                    }

                    await supabase.auth.signOut();
                    window.location.href = "/login";
                  } catch (e) {
                    console.error(e);
                    setWithdrawing(false);
                    showAlert(t("mypage.account.serverError"));
                  }
                }}
              >
                {withdrawing ? t("mypage.account.withdrawing") : t("mypage.account.confirmWithdraw")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
